import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type { NewsAutopostSettingsRead, NewsAutopostSettingsUpdate } from '../../types/api';

function path(businessId: string): string {
  return `/api/v1/businesses/${businessId}/news-autopost`;
}

/** The row, or its defaults before anything was saved. */
export function fetchNewsAutopost(
  businessId: string,
  signal?: AbortSignal,
): Promise<NewsAutopostSettingsRead> {
  return apiClient.get<NewsAutopostSettingsRead>(path(businessId), { signal });
}

/** Partial upsert. Owner only: a member gets 403 `OWNER_REQUIRED`. */
export function saveNewsAutopost(
  businessId: string,
  body: NewsAutopostSettingsUpdate,
): Promise<NewsAutopostSettingsRead> {
  return apiClient.put<NewsAutopostSettingsRead>(path(businessId), { body });
}

export interface NewsAutopostResult {
  /** Null until the first load for this business completes. */
  settings: NewsAutopostSettingsRead | null;
  isLoading: boolean;
  error: string | null;
  /** Put a row the server just returned in place, without a refetch. */
  replace: (settings: NewsAutopostSettingsRead) => void;
}

interface Loaded {
  businessId: string;
  settings: NewsAutopostSettingsRead | null;
  error: string | null;
}

/**
 * The daily news autopost's settings for one business.
 *
 * Same shape as every hand-rolled read here: the result is tagged with the
 * business it belongs to and the visible state derived during render, so a
 * brand switch never shows one brand's schedule under another's heading.
 */
export function useNewsAutopost(businessId: string | null): NewsAutopostResult {
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  useEffect(() => {
    if (!businessId) return;
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const settings = await fetchNewsAutopost(businessId, controller.signal);
        if (cancelled) return;
        setLoaded({ businessId, settings, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded({
          businessId,
          settings: null,
          error: getErrorMessage(caught, 'Could not read the daily autopost settings.'),
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId]);

  const replace = useCallback(
    (settings: NewsAutopostSettingsRead) => {
      if (!businessId) return;
      setLoaded({ businessId, settings, error: null });
    },
    [businessId],
  );

  const isCurrent = loaded !== null && loaded.businessId === businessId;
  return {
    settings: isCurrent ? loaded.settings : null,
    isLoading: businessId !== null && !isCurrent,
    error: isCurrent ? loaded.error : null,
    replace,
  };
}
